/**
 * Lightweight, client-side guard against a single email reserving too many seats
 * for the same showing.
 *
 * This is intentionally simple: it tracks the cumulative number of reserved seats per
 * (email address + showing) in the browser's localStorage. The limit applies per
 * showing, so a user may reserve up to the maximum again for a different showing.
 *
 * It is NOT a hard security boundary — a user can bypass it by clearing browser data,
 * using incognito mode or another device. A tamper-proof limit would require
 * server-side tracking (e.g. an email-based rate limit in the backend), which is out of
 * scope for the current ticket.
 */

export const MAX_RESERVED_SEATS_PER_EMAIL = 10;

const STORAGE_KEY = 'cineamo-reservation-seats';

const normalizeEmail = (email: string): string => email.trim().toLowerCase();

/** Storage key scoped to a single email + showing combination. */
const buildKey = (email: string, showingId: string): string => `${normalizeEmail(email)}::${showingId}`;

type ReservationQuotaStore = Record<string, number>;

function readStore(): ReservationQuotaStore {
    if (typeof window === 'undefined') {
        return {};
    }
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return {};
        }
        const parsed = JSON.parse(raw);
        return typeof parsed === 'object' && parsed !== null ? (parsed as ReservationQuotaStore) : {};
    } catch {
        return {};
    }
}

function writeStore(store: ReservationQuotaStore): void {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch {
        // Ignore write errors (e.g. storage disabled or full) — the guard is best-effort.
    }
}

/** Returns how many seats the given email has already reserved for the showing on this device. */
export function getReservedSeats(email: string, showingId: string): number {
    const store = readStore();
    const count = store[buildKey(email, showingId)];
    return typeof count === 'number' && count > 0 ? count : 0;
}

/** Adds the given amount of seats to the email's running total for the showing. */
export function addReservedSeats(email: string, showingId: string, seats: number): void {
    if (!Number.isFinite(seats) || seats <= 0) {
        return;
    }
    const store = readStore();
    store[buildKey(email, showingId)] = getReservedSeats(email, showingId) + seats;
    writeStore(store);
}
